import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import type { UserMe } from '@/entities';
import type { PermissionResponse } from '@/entities/permission';
import { STORAGE_KEYS } from '@/shared/config';
import { storage } from '../utils';

interface AuthState {
  // Auth
  user: UserMe | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Permissions
  permissions: PermissionResponse;

  // Auth actions
  setUser: (user: UserMe | null) => void;
  setToken: (token: string | null) => void;
  login: (user: UserMe, token: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;

  // Permission actions
  setPermissions: (permissions: PermissionResponse) => void;
  clearPermissions: () => void;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set) => ({
        // Auth state
        token: storage.get(STORAGE_KEYS.ACCESS_TOKEN) ?? null,
        user: null,
        isAuthenticated: !!storage.get(STORAGE_KEYS.ACCESS_TOKEN),
        isLoading: false,

        // Permission state
        permissions: [],

        // Auth actions
        setUser: (user) =>
          set((state) => ({
            user,
            isAuthenticated: !!state.token,
          })),

        setToken: (token) => set({ token, isAuthenticated: !!token }),

        login: (user, token) =>
          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
          }),

        logout: () => {
          storage.remove(STORAGE_KEYS.ACCESS_TOKEN);
          storage.remove(STORAGE_KEYS.REFRESH_TOKEN);

          set({
            user: null,
            token: null,
            isAuthenticated: false,
            permissions: [],
          });
        },

        setLoading: (isLoading) => set({ isLoading }),

        // Permission actions
        setPermissions: (permissions) =>
          set({
            permissions,
          }),

        clearPermissions: () =>
          set({
            permissions: [],
          }),
      }),
      {
        name: STORAGE_KEYS.AUTH_STATE,
        partialize: (state) => ({
          user: state.user,
          token: state.token,
          isAuthenticated: state.isAuthenticated,
          permissions: state.permissions,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
);
